import { Handle, NodeProps, Position, type Node } from "@xyflow/react";
import classNames from "classnames";
import React, { memo, useEffect, useRef, useState } from "react";
import { employeeRole, vacancyRole } from "../layout";
import { NodeData, initials, levelColor, plural, shortName, useTreeActions, useTreeState } from "./context";
import { GeneralIcon } from "modules/common/components/planr/icon/Generalcon";
import { CrownIcon, PencilIcon, PlusIcon, UnitTypeIcon } from "./icons";
import styles from "./OrgTree.module.scss";

type TreeNode = Node<NodeData>;

const Handles = () => (
    <>
        <Handle type="target" position={Position.Left} isConnectable={false} />
        <Handle type="source" position={Position.Right} isConnectable={false} />
    </>
);

/** Общие классы состояния узла: путь, поиск, перетаскивание, уход */
const useNodeClasses = (id: string, leaving: boolean) => {
    const state = useTreeState();
    const drop = state.drop?.id === id ? state.drop.mode : null;

    return classNames(styles.node, {
        [styles.path]: state.path.has(id) && state.hovered !== id,
        [styles.hovered]: state.hovered === id,
        [styles.flash]: state.flash === id,
        [styles.hit]: state.hits.has(id),
        [styles.dim]: state.dimmed?.has(id),
        [styles.leaving]: leaving,
        [styles.dragSource]: state.dragging === id,
        [styles.dropInside]: drop === "inside",
        [styles.dropBefore]: drop === "before",
        [styles.dropAfter]: drop === "after",
        [styles.dropInvalid]: drop === "invalid",
    });
};

const Knob = ({ id, open, count }: { id: string; open: boolean; count: number }) => {
    const actions = useTreeActions();
    return (
        <button
            type="button"
            className={classNames(styles.knob, "nodrag", { [styles.open]: open })}
            aria-expanded={open}
            aria-label={open ? "Свернуть" : "Развернуть"}
            title={open ? "Свернуть" : "Развернуть"}
            onClick={(e) => {
                e.stopPropagation();
                actions.toggle(id);
            }}
        >
            <GeneralIcon type="general-chevron-right" className={styles.knobIcon} />
            {!open && count > 0 && <span className={styles.count}>{count}</span>}
        </button>
    );
};

export const OrgNode = memo(({ data }: NodeProps<TreeNode>) => {
    const state = useTreeState();
    const actions = useTreeActions();
    const { layout, delay, leaving } = data;
    const stats = layout.item.stats;
    const cls = useNodeClasses(layout.id, leaving);

    return (
        <div className={classNames(cls, styles.org)} style={{ ["--delay" as any]: `${delay}ms` }}>
            <Handles />
            <div className={styles.orgTitle} title={state.companyName}>
                {state.companyName}
            </div>
            <div className={styles.orgSub}>Структура организации</div>
            <dl>
                <dt>Подразделений</dt>
                <dd>{stats.units}</dd>
                <dt>Сотрудников</dt>
                <dd>{stats.employees}</dd>
                <dt>Вакансий</dt>
                <dd>{stats.vacancies}</dd>
            </dl>
            {state.edit && (
                <div className={classNames(styles.quickAdd, "nodrag")} onClick={(e) => e.stopPropagation()}>
                    <button
                        type="button"
                        className={styles.addBtn}
                        title="Добавить подразделение верхнего уровня"
                        onClick={(e) => actions.addUnit(layout.item.id, e.currentTarget)}
                    >
                        <PlusIcon size={10} /> Подразделение
                    </button>
                </div>
            )}
            {layout.expandable && <Knob id={layout.id} open={layout.open} count={layout.item.children.length} />}
        </div>
    );
});

export const UnitNode = memo(({ data }: NodeProps<TreeNode>) => {
    const state = useTreeState();
    const actions = useTreeActions();
    const { layout, delay, leaving } = data;
    const unit = layout.item.unit!;
    const stats = layout.item.stats;
    const type = unit.unitTypeId ? state.typesById.get(unit.unitTypeId) : undefined;
    const manager = unit.managerId ? state.employeesById.get(unit.managerId) : undefined;
    const renaming = state.renaming === unit.id;
    const cls = useNodeClasses(layout.id, leaving);
    const direct = layout.item.children.length;
    const clickTimer = useRef<ReturnType<typeof setTimeout>>(undefined);

    useEffect(() => () => clearTimeout(clickTimer.current), []);

    return (
        <div
            className={classNames(cls, styles.unit)}
            style={{ ["--delay" as any]: `${delay}ms`, ["--level-color" as any]: levelColor(layout.depth) }}
            onClick={(e) => {
                if (renaming) {
                    return;
                }
                // в режиме правки двойной клик — переименование: карточку открываем,
                // только если второго клика не было
                if (!state.edit) {
                    actions.openUnit(unit.id);
                    return;
                }
                if (e.detail === 1) {
                    clickTimer.current = setTimeout(() => actions.openUnit(unit.id), 240);
                }
            }}
            onDoubleClick={(e) => {
                if (state.edit) {
                    e.stopPropagation();
                    clearTimeout(clickTimer.current);
                    actions.startRename(unit.id);
                }
            }}
            onContextMenu={(e) => actions.contextMenu(unit.id, e)}
            onMouseEnter={() => actions.hover(unit.id)}
            onMouseLeave={() => actions.hover(null)}
        >
            <Handles />
            <div className={styles.typeLine}>
                <UnitTypeIcon name={type?.icon ?? "unit"} className={styles.typeIcon} />
                <span>{type?.name ?? "Подразделение"}</span>
            </div>
            {renaming ? (
                <RenameInput value={unit.name} onDone={(name) => actions.commitRename(unit.id, name)} />
            ) : (
                <span className={styles.title} title={unit.name}>
                    {unit.name}
                </span>
            )}
            <div className={styles.unitFoot}>
                <div
                    className={classNames(styles.boss, { [styles.none]: !manager })}
                    title={manager ? `Руководитель: ${manager.name}` : undefined}
                >
                    {manager ? (
                        <>
                            <CrownIcon size={11} className={styles.bossIcon} />
                            <span className={styles.bossName}>{shortName(manager.name)}</span>
                        </>
                    ) : (
                        <span className={styles.bossName}>Нет руководителя</span>
                    )}
                </div>
                <div className={styles.metrics}>
                    <span className={styles.pill} title="Сотрудников во всей ветке">
                        {stats.employees} чел.
                    </span>
                    {stats.vacancies > 0 && (
                        <span
                            className={classNames(styles.pill, styles.vacPill)}
                            title="Незанятых ставок во всей ветке"
                        >
                            {stats.vacancies} вак.
                        </span>
                    )}
                    {state.edit && !!layout.item.staff?.over && (
                        <span
                            className={classNames(styles.pill, styles.overPill)}
                            title="Сотрудников этого подразделения сверх штата"
                        >
                            +{layout.item.staff.over} сверх штата
                        </span>
                    )}
                    {stats.units > 0 && (
                        <span
                            className={classNames(styles.pill, styles.unitsPill)}
                            title="Вложенных подразделений во всей ветке"
                        >
                            {stats.units} подр.
                        </span>
                    )}
                </div>
            </div>
            {state.edit && (
                <span className={styles.editHint} title="Двойной клик — переименовать">
                    <PencilIcon size={12} />
                </span>
            )}
            {state.edit && !renaming && (
                // добавить в это подразделение прямо с карточки: у большого отдела строка «+ добавить»
                // уезжает вниз за всеми сотрудниками
                <div className={classNames(styles.quickAdd, "nodrag")} onClick={(e) => e.stopPropagation()}>
                    <button
                        type="button"
                        className={styles.addBtn}
                        title={`Добавить подразделение в «${unit.name}»`}
                        onClick={(e) => actions.addUnit(unit.id, e.currentTarget)}
                    >
                        <PlusIcon size={10} /> Подразделение
                    </button>
                    {state.canEditEmployee && (
                        <button
                            type="button"
                            className={styles.addBtn}
                            title={`Добавить сотрудника в «${unit.name}»`}
                            onClick={() => actions.addEmployee(unit.id)}
                        >
                            <PlusIcon size={10} /> Сотрудник
                        </button>
                    )}
                </div>
            )}
            {layout.expandable && <Knob id={layout.id} open={layout.open} count={direct} />}
        </div>
    );
});

const RenameInput = ({ value, onDone }: { value: string; onDone: (name: string | null) => void }) => {
    const [text, setText] = useState(value);
    const ref = useRef<HTMLInputElement>(null);
    const done = useRef(false);

    useEffect(() => {
        ref.current?.focus();
        ref.current?.select();
    }, []);

    const finish = (name: string | null) => {
        if (done.current) {
            return;
        }
        done.current = true;
        onDone(name);
    };

    return (
        <input
            ref={ref}
            className={classNames(styles.titleInput, "nodrag")}
            value={text}
            maxLength={200}
            onChange={(e) => setText(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => {
                e.stopPropagation();
                if (e.key === "Enter") {
                    finish(text.trim() && text.trim() !== value ? text.trim() : null);
                } else if (e.key === "Escape") {
                    finish(null);
                }
            }}
            onBlur={() => finish(text.trim() && text.trim() !== value ? text.trim() : null)}
        />
    );
};

export const EmployeeNode = memo(({ data }: NodeProps<TreeNode>) => {
    const state = useTreeState();
    const actions = useTreeActions();
    const { layout, delay, leaving } = data;
    const employee = layout.item.employee!;
    const cls = useNodeClasses(layout.id, leaving);
    const src = employee.hasAvatar ? `${state.apiBase}/org-structure/avatar/${employee.id}` : null;
    const role = employeeRole(employee);
    // руководитель своего подразделения — первым в списке и с отметкой
    const isManager = !!layout.parentId && state.managerByUnit.get(layout.parentId) === employee.id;

    return (
        <div
            className={classNames(cls, styles.employee, {
                [styles.np]: employee.nonProduction,
                [styles.fired]: employee.fired,
            })}
            style={{ ["--delay" as any]: `${delay}ms` }}
            title={employee.nonProduction ? "Непроизводственные затраты (признак должности)" : undefined}
            onContextMenu={(e) => actions.contextMenu(employee.id, e)}
            onMouseEnter={() => actions.hover(employee.id)}
            onMouseLeave={() => actions.hover(null)}
        >
            <Handles />
            <div className={styles.avatar}>
                {src ? <img src={src} alt="" loading="lazy" draggable={false} /> : initials(employee.name)}
            </div>
            <div className={styles.head}>
                {state.canOpenEmployee ? (
                    <a
                        className={classNames(styles.fio, "nodrag")}
                        role="button"
                        tabIndex={0}
                        onClick={(e) => {
                            e.stopPropagation();
                            actions.openEmployee(employee.id);
                        }}
                        onKeyDown={(e) => e.key === "Enter" && actions.openEmployee(employee.id)}
                        title={employee.name}
                    >
                        {shortName(employee.name)}
                    </a>
                ) : (
                    <span className={styles.fio} title={employee.name}>
                        {shortName(employee.name)}
                    </span>
                )}
                {isManager && (
                    <span className={styles.crown} title="Руководитель подразделения">
                        <CrownIcon size={12} />
                    </span>
                )}
            </div>
            <div className={styles.role} title={role}>
                {state.canOpenPosition && employee.positionId && employee.positionName ? (
                    <>
                        <a
                            className={classNames(styles.positionLink, "nodrag")}
                            role="button"
                            tabIndex={0}
                            title="Открыть карточку должности"
                            onClick={(e) => {
                                e.stopPropagation();
                                actions.openPosition(employee.positionId!);
                            }}
                            onKeyDown={(e) => e.key === "Enter" && actions.openPosition(employee.positionId!)}
                        >
                            {employee.positionName}
                        </a>
                        {employee.gradeName && ` · ${employee.gradeName}`}
                    </>
                ) : (
                    role
                )}
            </div>
            {/* метки — ярлычками на верхней рамке, чтобы не отнимали место у ФИО и должности */}
            {(employee.nonProduction || employee.fired || employee.poohBah) && (
                <div className={styles.tags}>
                    {employee.nonProduction && (
                        <span className={classNames(styles.badge, styles.npBadge)}>Непроизв.</span>
                    )}
                    {employee.fired && <span className={classNames(styles.badge, styles.firedBadge)}>Уволен(а)</span>}
                    {!employee.fired && employee.poohBah && <span className={styles.badge}>Совместитель</span>}
                </div>
            )}
        </div>
    );
});

/** Незанятая ставка — пунктирная псевдо-карточка в списке сотрудников подразделения */
export const VacancyNode = memo(({ data }: NodeProps<TreeNode>) => {
    const state = useTreeState();
    const actions = useTreeActions();
    const { layout, delay, leaving } = data;
    const slot = layout.item.vacancy!;
    const unitId = layout.parentId;
    const cls = useNodeClasses(layout.id, leaving);
    const canHire = state.edit && state.canEditEmployee && !!unitId;

    return (
        <div
            className={classNames(cls, styles.employee, styles.vacancy, { [styles.clickable]: canHire })}
            style={{ ["--delay" as any]: `${delay}ms` }}
            title={canHire ? "Принять сотрудника на эту ставку" : "Незанятая ставка по штатному расписанию"}
            onClick={() => canHire && actions.addEmployee(unitId!, slot.positionId)}
            onContextMenu={(e) => actions.contextMenu(layout.id, e)}
            onMouseEnter={() => actions.hover(layout.id)}
            onMouseLeave={() => actions.hover(null)}
        >
            <Handles />
            <div className={styles.avatar}>
                <PlusIcon size={12} />
            </div>
            <div className={styles.head}>
                <span className={styles.fio}>Вакансия</span>
            </div>
            <div className={styles.role}>{vacancyRole(slot)}</div>
        </div>
    );
});

export const AddNode = memo(({ data }: NodeProps<TreeNode>) => {
    const state = useTreeState();
    const actions = useTreeActions();
    const { layout, delay } = data;
    const target = layout.item;
    const isUnit = target.kind === "unit";
    const name = isUnit ? target.unit?.name : "организацию";

    return (
        <div className={styles.addRow} style={{ ["--delay" as any]: `${delay}ms` }}>
            <Handles />
            <button
                type="button"
                className={classNames(styles.addBtn, "nodrag")}
                title={isUnit ? `Добавить подразделение в «${name}»` : "Добавить подразделение верхнего уровня"}
                onClick={(e) => actions.addUnit(target.id, e.currentTarget)}
            >
                <PlusIcon size={10} /> Подразделение
            </button>
            {isUnit && state.canEditEmployee && (
                <button
                    type="button"
                    className={classNames(styles.addBtn, "nodrag")}
                    title={`Добавить сотрудника в «${name}»`}
                    onClick={() => actions.addEmployee(target.id)}
                >
                    <PlusIcon size={10} /> Сотрудник
                </button>
            )}
        </div>
    );
});

export const nodeTypes = {
    org: OrgNode,
    unit: UnitNode,
    employee: EmployeeNode,
    vacancy: VacancyNode,
    add: AddNode,
};

export const describeUnit = (employees: number, units: number) =>
    [
        employees ? `${employees} ${plural(employees, "сотрудник", "сотрудника", "сотрудников")}` : "",
        units ? `${units} ${plural(units, "подразделение", "подразделения", "подразделений")}` : "",
    ]
        .filter(Boolean)
        .join(" и ");
